import { SectionShell } from "@/components/design-system/SectionShell";
import { Alert } from "@/components/ui/alert";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";

const code = `import { Alert } from "@/components/ui/alert";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";

<Alert tone="info" icon={<Info className="h-4 w-4" />} title="Heads up">
  An informational message with optional title and icon.
</Alert>
<Alert tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
  Validation passed — no anomalies detected.
</Alert>
<Alert tone="warning" icon={<TriangleAlert className="h-4 w-4" />} title="Gender mismatch">
  Tagged Male but the file's rows are UNISEX.
</Alert>
<Alert tone="danger" icon={<XCircle className="h-4 w-4" />} title="Duplicate file">
  Identical data to an upload already in the system.
</Alert>`;

export function AlertSection() {
  return (
    <SectionShell
      id="alert"
      title="Alert"
      description={
        <>
          A toned status banner with an optional leading icon and bold title.
          Four tones — <code>info</code>, <code>success</code>,{" "}
          <code>warning</code>, <code>danger</code> — for surfacing the outcome
          of a check or operation inline. Always use the primitive; never
          reach for ad-hoc colored panels.
        </>
      }
      whenToUse={
        <ul>
          <li>Validation / readiness results (pass, warning, failure).</li>
          <li>Operation feedback that should persist on the page (not a toast).</li>
          <li>Contextual notices tied to a specific panel or section.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Transient flash messages — use the global flash/toast.</li>
          <li>A toneless container — use <code>.callout</code>.</li>
          <li>A tiny inline status tag — use <code>Badge</code>.</li>
        </ul>
      }
      preview={
        <div className="flex w-full flex-col gap-3">
          <Alert tone="info" icon={<Info className="h-4 w-4" />} title="Heads up">
            An informational message with optional title and icon.
          </Alert>
          <Alert tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
            Validation passed — no anomalies detected.
          </Alert>
          <Alert
            tone="warning"
            icon={<TriangleAlert className="h-4 w-4" />}
            title="Gender mismatch"
          >
            Tagged Male but the file's rows are UNISEX.
          </Alert>
          <Alert
            tone="danger"
            icon={<XCircle className="h-4 w-4" />}
            title="Duplicate file"
          >
            Identical data to an upload already in the system.
          </Alert>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>tone</code>: <code>info</code> (default) | <code>success</code>{" "}
            | <code>warning</code> | <code>danger</code>
          </li>
          <li><code>icon</code>: optional leading element, vertically aligned to the first line.</li>
          <li><code>title</code>: optional bold heading rendered above the body.</li>
        </ul>
      }
    />
  );
}
